import type { ExerciseTemplate } from '@yuan-math/exercises';
import { roundTo } from '@yuan-math/math';
import { choiceExercise, numericExercise, options, template, trap } from './authoring.ts';

/** The value of π these exercises use, stated in every prompt that needs it. */
const PI = 3.14;

/** Transformations, and the volumes of the round solids. */
export const GEOMETRY_EIGHT_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'transformation-translate',
    conceptId: 'transformations',
    description: 'Translate a point and read off a coordinate.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const x = rng.int(-5, 5);
      const y = rng.int(-5, 5);
      const dx = rng.int(1, difficulty === 2 ? 4 : 7) * (rng.bool() ? 1 : -1);
      const dy = rng.int(1, difficulty === 2 ? 4 : 7) * (rng.bool() ? 1 : -1);
      const wantX = rng.bool();
      const answer = wantX ? x + dx : y + dy;
      const swapped = wantX ? y + dy : x + dx;

      return numericExercise({
        prompt: `The point (${x}, ${y}) is translated ${Math.abs(dx)} ${dx > 0 ? 'right' : 'left'} and ${Math.abs(dy)} ${dy > 0 ? 'up' : 'down'}. What is the ${wantX ? 'x' : 'y'}-coordinate of the image?`,
        figure: {
          kind: 'coordinate-grid',
          min: -10,
          max: 10,
          points: [
            { x, y, label: 'A' },
            { x: x + dx, y: y + dy, label: "A'" },
          ],
        },
        value: answer,
        summary: `A translation adds the same amount to every point: (${x} + ${dx}, ${y} + ${dy}) = (${x + dx}, ${y + dy}).`,
        hint: 'Left and right change x; up and down change y. Nothing else about the shape changes at all.',
        steps: [
          { label: 'Add the horizontal shift to x.', latex: `${x} + (${dx}) = ${x + dx}` },
          { label: 'Add the vertical shift to y.', latex: `${y} + (${dy}) = ${y + dy}` },
        ],
        traps: [...(swapped === answer ? [] : [trap(swapped, 'coordinates-reversed')])],
      });
    },
  }),

  template({
    id: 'transformation-reflect',
    conceptId: 'transformations',
    description: 'Reflect a point in an axis.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const x = rng.int(1, 7) * (rng.bool() ? 1 : -1);
      const y = rng.int(1, 7) * (rng.bool() ? 1 : -1);
      const overX = rng.bool();
      const image = overX ? { x, y: -y } : { x: -x, y };
      const wanted = overX ? image.y : image.x;
      const unchanged = overX ? x : y;

      return numericExercise({
        prompt: `The point (${x}, ${y}) is reflected in the ${overX ? 'x' : 'y'}-axis. What is the ${overX ? 'y' : 'x'}-coordinate of the image?`,
        figure: {
          kind: 'coordinate-grid',
          min: -8,
          max: 8,
          points: [
            { x, y, label: 'A' },
            { x: image.x, y: image.y, label: "A'" },
          ],
        },
        value: wanted,
        summary: `Reflecting in the ${overX ? 'x' : 'y'}-axis flips the sign of the ${overX ? 'y' : 'x'}-coordinate and leaves the other alone: (${image.x}, ${image.y}).`,
        hint: `A mirror in the ${overX ? 'x' : 'y'}-axis moves a point straight ${overX ? 'up or down' : 'across'} to the other side, the same distance away.`,
        steps: [
          { label: `The ${overX ? 'x' : 'y'}-coordinate is unchanged.`, latex: `${unchanged}` },
          { label: `The ${overX ? 'y' : 'x'}-coordinate changes sign.`, latex: `${wanted}` },
        ],
        traps: [...(unchanged === wanted ? [] : [trap(unchanged, 'coordinates-reversed')])],
      });
    },
  }),

  template({
    id: 'transformation-congruent-or-similar',
    conceptId: 'transformations',
    description: 'Decide whether a transformation preserves size as well as shape.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const dilation = rng.bool();
      const factor = rng.int(2, 4);
      const move = rng.pick(['translated 4 units right', 'rotated 90° about the origin', 'reflected in the y-axis']);

      const { choices, correctId } = options(
        [
          {
            label: dilation ? 'Similar, but not congruent' : 'Congruent — identical in both shape and size',
            correct: true,
          },
          {
            label: dilation ? 'Congruent — identical in both shape and size' : 'Similar, but not congruent',
            misconceptionId: 'dilation-called-congruent',
          },
          { label: 'Neither similar nor congruent', misconceptionId: 'dilation-called-congruent' },
          { label: 'Congruent only if the shape is a triangle', misconceptionId: 'dilation-called-congruent' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: dilation
          ? `A triangle is enlarged by a scale factor of ${factor}. How does the image compare with the original?`
          : `A triangle is ${move}. How does the image compare with the original?`,
        choices,
        correctId,
        summary: dilation
          ? `Every length is multiplied by ${factor}, so the angles are unchanged but the size is not. That is similarity, not congruence.`
          : 'Translations, rotations and reflections move a figure without resizing it, so the image is congruent to the original.',
        hint: 'Congruent means you could lay one exactly on top of the other. Which of these changes the size?',
      });
    },
  }),

  template({
    id: 'cylinder-volume',
    conceptId: 'round-solids',
    description: 'Find the volume of a cylinder.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const radius = rng.int(2, difficulty === 4 ? 10 : 6);
      const height = rng.int(2, difficulty === 4 ? 15 : 9);
      const answer = roundTo(PI * radius * radius * height, 2);

      return numericExercise({
        prompt: `A cylinder has a radius of ${radius} cm and a height of ${height} cm. What is its volume? Use π = ${PI}.`,
        unit: 'cubic cm',
        figure: { kind: 'solid', solid: 'cylinder', labels: { radius: `${radius} cm`, height: `${height} cm` } },
        value: answer,
        summary: `The circular base has area π × ${radius}² = ${roundTo(PI * radius * radius, 2)} cm², and stacking it ${height} cm high gives ${answer} cubic cm.`,
        hint: 'A cylinder is a circle given a height. Find the area of the base, then multiply by how tall it is.',
        steps: [
          { label: 'Area of the circular base.', latex: `\\pi r^2 = ${PI} \\times ${radius * radius} = ${roundTo(PI * radius * radius, 2)}` },
          { label: 'Multiply by the height.', latex: `${roundTo(PI * radius * radius, 2)} \\times ${height} = ${answer}` },
        ],
        traps: [
          /*
           * At radius 3 these two coincide — πrh and πr²h ÷ 3 are the same
           * number — so only one can be offered, and the cone/cylinder mix-up
           * is the more characteristic error in this unit.
           */
          ...(radius === 3 ? [] : [trap(roundTo(PI * radius * height, 2), 'radius-wrong-power')]),
          trap(roundTo((PI * radius * radius * height) / 3, 2), 'cone-third-omitted'),
        ],
      });
    },
  }),

  template({
    id: 'cone-volume',
    conceptId: 'round-solids',
    description: 'Find the volume of a cone, where the third is the whole point.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const radius = rng.int(2, 9);
      // A height divisible by three keeps the arithmetic honest rather than
      // burying the idea under rounding.
      const height = rng.int(1, 5) * 3;
      const cylinder = PI * radius * radius * height;
      const answer = roundTo(cylinder / 3, 2);

      return numericExercise({
        prompt: `A cone has a radius of ${radius} cm and a height of ${height} cm. What is its volume? Use π = ${PI}.`,
        unit: 'cubic cm',
        figure: { kind: 'solid', solid: 'cone', labels: { radius: `${radius} cm`, height: `${height} cm` } },
        value: answer,
        summary: `The cylinder around it holds ${roundTo(cylinder, 2)} cubic cm, and a cone holds exactly a third of that: ${answer} cubic cm.`,
        hint: 'Work out the cylinder with the same base and height first. A cone is one third of it.',
        steps: [
          { label: 'The cylinder around it.', latex: `\\pi r^2 h = ${roundTo(cylinder, 2)}` },
          { label: 'A cone is one third.', latex: `\\frac{1}{3} \\times ${roundTo(cylinder, 2)} = ${answer}` },
        ],
        traps: [trap(roundTo(cylinder, 2), 'cone-third-omitted')],
      });
    },
  }),

  template({
    id: 'sphere-volume',
    conceptId: 'round-solids',
    description: 'Find the volume of a sphere.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const radius = rng.int(2, 9);
      const answer = roundTo((4 / 3) * PI * radius ** 3, 2);

      return numericExercise({
        prompt: `A sphere has a radius of ${radius} cm. What is its volume? Use π = ${PI}.`,
        unit: 'cubic cm',
        figure: { kind: 'solid', solid: 'sphere', labels: { radius: `${radius} cm` } },
        value: answer,
        summary: `V = ⁴⁄₃πr³ = ⁴⁄₃ × ${PI} × ${radius ** 3} = ${answer} cubic cm.`,
        hint: 'A volume fills three dimensions, so the radius appears three times, not twice.',
        steps: [
          { label: 'Cube the radius.', latex: `${radius}^3 = ${radius ** 3}` },
          { label: 'Multiply by ⁴⁄₃π.', latex: `\\frac{4}{3} \\times ${PI} \\times ${radius ** 3} = ${answer}` },
        ],
        traps: [
          trap(roundTo((4 / 3) * PI * radius * radius, 2), 'radius-wrong-power'),
          trap(roundTo(PI * radius ** 3, 2), 'cone-third-omitted'),
        ],
      });
    },
  }),

  /* ---------------------------------------------------- angles and shape */
  template({
    id: 'angle-relationships-transversal',
    conceptId: 'angle-relationships',
    description: 'Find an angle made by a transversal crossing two parallel lines.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      // Never 90°, where every pair agrees and the question tests nothing.
      const given = rng.pick([35, 41, 48, 52, 58, 63, 68, 72, 105, 112, 118, 124, 131, 138, 142, 149]);
      const relation = rng.pick(
        difficulty === 2 ? (['corresponding', 'alternate'] as const) : (['corresponding', 'alternate', 'co-interior'] as const),
      );
      const answer = relation === 'co-interior' ? 180 - given : given;
      const wording = {
        corresponding: 'in the same corner at the other crossing',
        alternate: 'alternate to it — between the lines, on the other side of the transversal',
        'co-interior': 'co-interior with it — between the lines, on the same side of the transversal',
      }[relation];

      return numericExercise({
        prompt: `A transversal crosses two parallel lines, making an angle of ${given}°. How big is the angle ${wording}?`,
        unit: 'degrees',
        figure: {
          kind: 'parallel-lines',
          mark: relation === 'corresponding' ? [1, 5] : relation === 'alternate' ? [4, 6] : [3, 6],
        },
        value: answer,
        summary:
          relation === 'co-interior'
            ? `Angles on the same side of the transversal make a straight line together, so they add to 180°: 180 − ${given} = ${answer}°.`
            : `The two crossings are identical, so ${relation} angles are equal — the other angle is also ${answer}°.`,
        hint: 'Two of the three pairs are equal and one adds to 180°. Which side of the transversal are these two angles on?',
        steps: [
          {
            label: relation === 'co-interior' ? 'Same side, so subtract from 180°.' : 'Equal angles.',
            latex: relation === 'co-interior' ? `180 - ${given} = ${answer}` : `${answer}`,
          },
        ],
        traps: [
          relation === 'co-interior' ? trap(given, 'co-interior-taken-equal') : trap(180 - given, 'angle-pair-misnamed'),
        ],
      });
    },
  }),

  template({
    id: 'angle-relationships-triangle',
    conceptId: 'angle-relationships',
    description: 'Find the third angle of a triangle, or the exterior angle beside it.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const first = rng.int(25, 80);
      const second = rng.int(25, 155 - first);
      const third = 180 - first - second;
      // At difficulty 3 the question asks for the exterior angle, which is the
      // two given angles added — the same figure, one step further on.
      const wantExterior = difficulty === 3;
      const answer = wantExterior ? first + second : third;

      return numericExercise({
        prompt: wantExterior
          ? `Two angles of a triangle are ${first}° and ${second}°. One side is extended past the third vertex. How big is the exterior angle there?`
          : `Two angles of a triangle are ${first}° and ${second}°. How big is the third angle?`,
        unit: 'degrees',
        value: answer,
        summary: wantExterior
          ? `The third angle is 180 − ${first} − ${second} = ${third}°, and the exterior angle completes a straight line with it: 180 − ${third} = ${answer}°. That is the two given angles added, which is always true.`
          : `The angles of any triangle add to 180°, so the third is 180 − ${first} − ${second} = ${answer}°.`,
        hint: wantExterior
          ? 'An exterior angle equals the two interior angles it does not touch. Check it the long way if you like — both routes agree.'
          : 'A triangle sums to 180°, not 360°. A full turn around a point is the one that comes to 360°.',
        steps: wantExterior
          ? [
              { label: 'Find the third angle.', latex: `180 - ${first} - ${second} = ${third}` },
              { label: 'The exterior angle completes a straight line.', latex: `180 - ${third} = ${answer}` },
            ]
          : [{ label: 'Subtract both from 180°.', latex: `180 - ${first} - ${second} = ${answer}` }],
        traps: [
          ...(wantExterior
            ? [...(third === answer ? [] : [trap(third, 'exterior-angle-taken-as-interior')])]
            : [...(360 - first - second === answer ? [] : [trap(360 - first - second, 'angle-sum-confused')])]),
        ],
      });
    },
  }),

  template({
    id: 'angle-relationships-similar-side',
    conceptId: 'angle-relationships',
    description: 'Use a scale factor between similar triangles to find a missing side.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const factor = rng.int(2, 5);
      const known = rng.int(2, 9);
      const partner = known * factor;
      const other = rng.int(2, 9);
      const enlarging = rng.bool();
      // Enlarging multiplies; reducing divides. The other direction is the
      // classic error and it is what the trap catches.
      const answer = enlarging ? other * factor : other;
      const from = enlarging ? other : other * factor;
      const wrong = enlarging ? other / factor : other * factor * factor;

      return numericExercise({
        prompt: `Two triangles have two pairs of equal angles, so they are similar. A side of ${known} cm in the smaller one matches a side of ${partner} cm in the larger. ${enlarging ? `Another side of the smaller triangle is ${from} cm. How long is the matching side of the larger one?` : `Another side of the larger triangle is ${from} cm. How long is the matching side of the smaller one?`}`,
        unit: 'cm',
        value: answer,
        summary: `The scale factor is ${partner} ÷ ${known} = ${factor}. Going ${enlarging ? 'up to the larger triangle multiplies' : 'down to the smaller triangle divides'}: ${enlarging ? `${from} × ${factor}` : `${from} ÷ ${factor}`} = ${answer} cm.`,
        hint: 'Find the scale factor from the pair you were given, then check which triangle you are heading towards — larger means multiply, smaller means divide.',
        steps: [
          { label: 'Find the scale factor.', latex: `\\frac{${partner}}{${known}} = ${factor}` },
          {
            label: enlarging ? 'Multiply, because the image is larger.' : 'Divide, because the image is smaller.',
            latex: enlarging ? `${from} \\times ${factor} = ${answer}` : `\\frac{${from}}{${factor}} = ${answer}`,
          },
        ],
        traps: [...(wrong === answer ? [] : [trap(wrong, 'scale-factor-inverted')])],
      });
    },
  }),

  template({
    id: 'angle-relationships-aa',
    conceptId: 'angle-relationships',
    description: 'Say what two pairs of equal angles establish, and what they do not.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const first = rng.int(30, 70);
      const second = rng.int(30, 145 - first);

      const { choices, correctId } = options(
        [
          { label: 'They are similar — the same shape, at some scale factor', correct: true },
          { label: 'They are congruent — identical in shape and size', misconceptionId: 'angles-alone-give-congruence' },
          { label: 'Nothing follows until a pair of sides is known too', misconceptionId: 'angles-alone-give-congruence' },
          { label: 'They are similar only if the third angles are also checked', misconceptionId: 'third-angle-not-forced' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Two triangles each have an angle of ${first}° and an angle of ${second}°. What follows?`,
        choices,
        correctId,
        summary: `Two matching angles force the third, since all three add to 180° — here ${180 - first - second}° in both. Equal angles fix the shape but say nothing about the size, so the triangles are similar and need not be congruent.`,
        hint: 'How many angles of a triangle can you choose freely? And does knowing every angle tell you how big the triangle is?',
      });
    },
  }),
];
